import { createContext, useContext, useState, type ReactNode } from 'react';
import type { Chain, ChainInfo } from '../types';
import { CHAINS, DEFAULT_CHAIN } from '../utils/config';

interface ChainContextType {
  selectedChain: ChainInfo;
  setSelectedChain: (chain: Chain) => Promise<void>;
  chains: ChainInfo[];
}

const ChainContext = createContext<ChainContextType | undefined>(undefined);

// Add type declaration for window.ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}

export const ChainProvider = ({ children }: { children: ReactNode }) => {
  const [selectedChain, setSelectedChainState] = useState<ChainInfo>(
    CHAINS.find((c) => c.key === DEFAULT_CHAIN) || CHAINS[0]
  );

  const switchWalletNetwork = async (chainInfo: ChainInfo) => {
    if (!window.ethereum) {
      console.warn('No wallet detected');
      return;
    }

    // Check if we're on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('Attempting to switch wallet network:', { chain: chainInfo.name, isMobile });

    try {
      // Try to switch to the network
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainInfo.chainId.toString(16)}` }],
      });
      console.log(`Successfully switched to ${chainInfo.name} network`);
    } catch (switchError: any) {
      console.log('Switch error details:', switchError);
      
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          const chainParams = {
            chainId: `0x${chainInfo.chainId.toString(16)}`,
            chainName: chainInfo.name,
            nativeCurrency: {
              name: 'Ink',
              symbol: 'INK',
              decimals: 18,
            },
            rpcUrls: [
              'https://old-young-fog.ink-mainnet.quiknode.pro/921654dbc67e6b99bcdf05a56d80d4c28858aba7/',
            ],
            blockExplorerUrls: [chainInfo.explorerUrl],
          };

          console.log('Adding network with params:', chainParams);
          
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [chainParams],
          });
          console.log(`Successfully added and switched to ${chainInfo.name} network`);
        } catch (addError) {
          console.error(`Failed to add ${chainInfo.name} network:`, addError);
          
          // On mobile, some wallets might not support wallet_addEthereumChain
          if (isMobile) {
            console.warn('Mobile wallet may not support automatic network addition. User may need to add network manually.');
          }
        }
      } else {
        console.error(`Failed to switch to ${chainInfo.name} network:`, switchError);
        
        // On mobile, some wallets might have different error codes or behavior
        if (isMobile) {
          console.warn('Mobile wallet network switching failed. User may need to switch manually.');
        }
      }
    }
  };

  const setSelectedChain = async (chain: Chain) => {
    const found = CHAINS.find((c) => c.key === chain);
    if (found) {
      setSelectedChainState(found);
      
      // Automatically switch wallet network when platform network changes
      // Add a small delay to ensure the state update completes first
      setTimeout(async () => {
        await switchWalletNetwork(found);
      }, 100);
    }
  };

  return (
    <ChainContext.Provider value={{ selectedChain, setSelectedChain, chains: CHAINS }}>
      {children}
    </ChainContext.Provider>
  );
};

export const useChain = () => {
  const ctx = useContext(ChainContext);
  if (!ctx) throw new Error('useChain must be used within a ChainProvider');
  return ctx;
}; 